"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Type } from "lucide-react";

/**
 * Font Size Toggle Component (WCAG 2.2 AA)
 * 
 * Allows users to adjust text size for better readability.
 * Persists preference to localStorage.
 * 
 * TOR Reference: Accessibility requirement for vision-impaired users
 */

const MIN_SIZE = 80;
const MAX_SIZE = 150;
const STEP = 10;
const DEFAULT_SIZE = 100;
const STORAGE_KEY = "customs-font-size";

interface FontSizeToggleProps {
    showLabel?: boolean;
    variant?: "default" | "compact";
}

export function FontSizeToggle({
    showLabel = false,
    variant = "default"
}: FontSizeToggleProps) {
    const [fontSize, setFontSize] = useState<number>(DEFAULT_SIZE);
    const [mounted, setMounted] = useState(false);

    // Load saved preference on mount
    useEffect(() => {
        setMounted(true);
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            const size = parseInt(saved, 10);
            if (size >= MIN_SIZE && size <= MAX_SIZE) {
                setFontSize(size);
                applyFontSize(size);
            }
        }
    }, []);

    // Apply font size to document
    const applyFontSize = (size: number) => {
        document.documentElement.style.fontSize = `${size}%`;
    };

    // Handle size change
    const handleSizeChange = (delta: number) => {
        const newSize = Math.min(MAX_SIZE, Math.max(MIN_SIZE, fontSize + delta));
        setFontSize(newSize);
        applyFontSize(newSize);
        localStorage.setItem(STORAGE_KEY, newSize.toString());
    };

    // Reset to default
    const handleReset = () => {
        setFontSize(DEFAULT_SIZE);
        applyFontSize(DEFAULT_SIZE);
        localStorage.setItem(STORAGE_KEY, DEFAULT_SIZE.toString());
    };

    // Prevent hydration mismatch
    if (!mounted) {
        return null;
    }

    if (variant === "compact") {
        return (
            <div
                role="group"
                aria-label="ปรับขนาดตัวอักษร"
                className="flex items-center gap-1"
            >
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSizeChange(-STEP)}
                    disabled={fontSize <= MIN_SIZE}
                    aria-label="ลดขนาดตัวอักษร"
                    className="h-8 w-8 p-0"
                >
                    <span className="text-sm font-medium">A-</span>
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSizeChange(STEP)}
                    disabled={fontSize >= MAX_SIZE}
                    aria-label="เพิ่มขนาดตัวอักษร"
                    className="h-8 w-8 p-0"
                >
                    <span className="text-lg font-medium">A+</span>
                </Button>
            </div>
        );
    }

    return (
        <div
            role="group"
            aria-label="ปรับขนาดตัวอักษร"
            className="flex items-center gap-2 p-2 rounded-lg bg-gray-100 dark:bg-gray-800"
        >
            {showLabel && (
                <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 mr-2">
                    <Type className="h-4 w-4" />
                    <span>ขนาดตัวอักษร</span>
                </div>
            )}

            <Button
                variant="outline"
                size="icon"
                onClick={() => handleSizeChange(-STEP)}
                disabled={fontSize <= MIN_SIZE}
                aria-label="ลดขนาดตัวอักษร"
                className="h-8 w-8"
            >
                <Minus className="h-4 w-4" />
            </Button>

            <button
                onClick={handleReset}
                className="min-w-[3rem] text-center text-sm font-medium px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                aria-label={`ขนาดปัจจุบัน ${fontSize}% คลิกเพื่อรีเซ็ต`}
            >
                {fontSize}%
            </button>

            <Button
                variant="outline"
                size="icon"
                onClick={() => handleSizeChange(STEP)}
                disabled={fontSize >= MAX_SIZE}
                aria-label="เพิ่มขนาดตัวอักษร"
                className="h-8 w-8"
            >
                <Plus className="h-4 w-4" />
            </Button>
        </div>
    );
}
